'use client';

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ExternalLink, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TournamentRulesFormat } from '../interfaces/tournaments';
import { cn } from '@/lib/utils';

interface TournamentRulesViewerProps {
  rules?: string | null;
  rulesFormat?: TournamentRulesFormat | null;
  rulesPdfUrl?: string | null;
  className?: string;
}

export function hasTournamentRules(
  rules?: string | null,
  rulesFormat?: TournamentRulesFormat | null,
  rulesPdfUrl?: string | null,
): boolean {
  if (rulesFormat === 'pdf') return Boolean(rulesPdfUrl);
  return Boolean(rules?.trim());
}

export const TournamentRulesViewer: React.FC<TournamentRulesViewerProps> = ({
  rules,
  rulesFormat = 'markdown',
  rulesPdfUrl,
  className,
}) => {
  if (rulesFormat === 'pdf' && rulesPdfUrl) {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm text-muted-foreground">Reglamento en PDF</span>
        <Button variant="outline" size="sm" asChild className="ml-auto">
          <a href={rulesPdfUrl} target="_blank" rel="noopener noreferrer">
            Ver reglas
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
        </Button>
      </div>
    );
  }

  if (!rules?.trim()) {
    return null;
  }

  return (
    <div
      className={cn(
        'tournament-rules-markdown rounded-xl border border-border/60 bg-card/30 p-5 sm:p-6',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{rules}</ReactMarkdown>
    </div>
  );
};

export default TournamentRulesViewer;
